import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Typography, IconButton, TextField, CircularProgress, Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import DeleteIcon from "@mui/icons-material/Delete";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DownloadIcon from "@mui/icons-material/Download";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VideocamIcon from "@mui/icons-material/Videocam";
import { AuthContext } from "../context/AuthContext";
import { useTour } from "../context/TourContext";
import Header from "../components/Header";
import {
  getMyMedia, uploadImage, deleteMedia, updateLabel, getMediaUrl,
  triggerSingleDownload, downloadMediaZip,
} from "../service/mediaApi";
import { useSnack } from "../hooks/useSnack";

// ─── Lightbox ────────────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);

  const goPrev = useCallback(() => setCurrent((i) => (i - 1 + images.length) % images.length), [images.length]);
  const goNext = useCallback(() => setCurrent((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, goPrev, goNext]);

  const img = images[current];

  return (
    <Box onClick={onClose} sx={{
      position: "fixed", inset: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      backgroundColor: "rgba(0,0,0,0.9)", backdropFilter: "blur(8px)",
    }}>
      <IconButton onClick={onClose} sx={{
        position: "absolute", top: 16, right: 16, color: "white",
        bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, zIndex: 10001,
      }}>
        <CloseIcon />
      </IconButton>

      {images.length > 1 && (
        <IconButton onClick={(e) => { e.stopPropagation(); goPrev(); }} sx={{
          position: "absolute", left: 16, color: "white",
          bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, zIndex: 10001,
        }}>
          <ArrowBackIosNewIcon />
        </IconButton>
      )}

      <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: "90vw", maxHeight: "90vh" }}>
        {img.mime_type?.startsWith("video/") ? (
          <Box key={img.ID_media} component="video"
            src={getMediaUrl(img.public_id)}
            controls autoPlay
            sx={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: 2, boxShadow: "0 8px 60px rgba(0,0,0,0.8)" }}
          />
        ) : (
          <Box component="img" src={getMediaUrl(img.public_id)} alt={img.label || "image"} sx={{
            maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain",
            borderRadius: 2, boxShadow: "0 8px 60px rgba(0,0,0,0.8)",
            border: "2px solid rgba(255,255,255,0.15)",
          }} />
        )}
        {img.label && (
          <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.85)", fontSize: 14, fontStyle: "italic" }}>
            {img.label}
          </Typography>
        )}
        {images.length > 1 && (
          <Typography sx={{ mt: 0.5, color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
            {current + 1} / {images.length}
          </Typography>
        )}
      </Box>

      {images.length > 1 && (
        <IconButton onClick={(e) => { e.stopPropagation(); goNext(); }} sx={{
          position: "absolute", right: 16, color: "white",
          bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, zIndex: 10001,
        }}>
          <ArrowForwardIosIcon />
        </IconButton>
      )}
    </Box>
  );
}

// ─── Carte média (image ou vidéo) ─────────────────────────────────────────────
function PhotoCard({ img, onDelete, onLabelSaved, onZoom, onDownload, selectMode, isSelected, onToggleSelect, theme }) {
  const isVideo = img.mime_type?.startsWith("video/");
  const [editing, setEditing] = useState(false);
  const [labelVal, setLabelVal] = useState(img.label || "");

  const saveLabel = async () => {
    try {
      await updateLabel(img.ID_media, labelVal);
      onLabelSaved(img.ID_media, labelVal);
      setEditing(false);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleCardClick = () => {
    if (selectMode) onToggleSelect();
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        position: "relative", textAlign: "center",
        cursor: selectMode ? "pointer" : "default",
        borderRadius: 1,
        outline: selectMode && isSelected ? `2px solid ${theme.custom.mymodal.button}` : "2px solid transparent",
        transition: "outline 0.12s",
        "&:hover .action-btns": { opacity: selectMode ? 0 : 1 },
        "&:hover .media-thumb": { filter: selectMode ? (isSelected ? "brightness(0.65)" : "brightness(0.88)") : "brightness(0.85)" },
      }}
    >
      {/* Checkbox overlay (mode sélection) */}
      {selectMode && (
        <Box sx={{
          position: "absolute", top: 6, left: 6, zIndex: 5,
          bgcolor: "rgba(0,0,0,0.35)", borderRadius: "50%",
        }}>
          <Checkbox
            checked={isSelected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ color: "white", "&.Mui-checked": { color: theme.custom.mymodal.button }, p: 0.3 }}
          />
        </Box>
      )}

      {/* Boutons overlay (mode normal) */}
      {!selectMode && (
        <Box className="action-btns" sx={{
          position: "absolute", top: 6, left: 6, right: 6,
          display: "flex", justifyContent: "space-between",
          opacity: 0, transition: "opacity 0.18s ease", zIndex: 5,
        }}>
          <IconButton size="small" onClick={() => onZoom()} sx={{
            color: "white", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
            border: "1px solid rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(25,118,210,0.6)" },
          }}>
            {isVideo ? <PlayCircleOutlineIcon fontSize="small" /> : <ZoomInIcon fontSize="small" />}
          </IconButton>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownload(); }} sx={{
              color: "white", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
              border: "1px solid rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(76,175,80,0.6)" },
            }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Supprimer définitivement ce${isVideo ? "tte vidéo" : "tte photo"} ?`)) onDelete(img.ID_media);
            }} sx={{
              color: "white", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
              border: "1px solid rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(244,67,54,0.6)" },
            }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Miniature */}
      {isVideo ? (
        <Box className="media-thumb" onClick={(e) => { if (!selectMode) { e.stopPropagation(); onZoom(); } }} sx={{
          width: "100%", height: 180, borderRadius: 1,
          bgcolor: "rgba(0,0,0,0.75)",
          border: `1px solid ${theme.custom.mymodal.border.replace("1px solid ", "")}`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5,
          cursor: selectMode ? "pointer" : "zoom-in", transition: "filter 0.18s ease",
        }}>
          <PlayCircleOutlineIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.85)" }} />
          <VideocamIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }} />
        </Box>
      ) : (
        <Box component="img"
          className="media-thumb"
          src={getMediaUrl(img.public_id)}
          alt={img.label || "photo"}
          loading="lazy"
          onClick={(e) => { if (!selectMode) { e.stopPropagation(); onZoom(); } }}
          sx={{
            width: "100%", height: 180, objectFit: "cover", borderRadius: 1,
            border: `1px solid ${theme.custom.mymodal.border.replace("1px solid ", "")}`,
            cursor: selectMode ? "pointer" : "zoom-in",
            display: "block", transition: "filter 0.18s ease",
          }}
        />
      )}

      {/* Label éditable (désactivé en mode sélection) */}
      {!selectMode && (
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, px: 0.5 }}>
          {editing ? (
            <>
              <TextField
                size="small" value={labelVal}
                onChange={(e) => setLabelVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveLabel(); if (e.key === "Escape") setEditing(false); }}
                autoFocus
                sx={{
                  flex: 1,
                  "& input": { color: theme.custom.mymodal.text, fontSize: "0.75rem", py: 0.3, px: 0.8 },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.custom.mymodal.text + "55" },
                }}
              />
              <IconButton size="small" onClick={saveLabel} sx={{ color: "#4ade80", p: 0.3 }}><CheckIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => { setLabelVal(img.label || ""); setEditing(false); }} sx={{ color: theme.custom.mymodal.text, p: 0.3 }}><CloseIcon fontSize="small" /></IconButton>
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, cursor: "pointer" }} onClick={() => setEditing(true)}>
              <Typography sx={{ fontSize: "0.75rem", fontStyle: "italic", color: theme.custom.mymodal.text, opacity: 0.8 }}>
                {img.label || <span style={{ opacity: 0.4 }}>Ajouter un label</span>}
              </Typography>
              <EditIcon sx={{ fontSize: "0.8rem", color: theme.custom.mymodal.text, opacity: 0.4 }} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Page Galerie ─────────────────────────────────────────────────────────────
export default function Gallery() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state: user } = useContext(AuthContext);
  const { startTour } = useTour();
  const { showSnack, Snack } = useSnack();

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Pending uploads
  const [pending, setPending] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total } ou null
  const fileInputRef = useRef(null);

  // Mode sélection
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // Label refs pour les inputs de pending (uncontrolled — évite le unfocus)
  const labelRefs = useRef([]);

  // Infinite scroll
  const BATCH = 15;
  const [visibleCount, setVisibleCount] = useState(BATCH);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((n) => n + BATCH);
      },
      { rootMargin: "600px" } // precharge 600px avant d'arriver en bas
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [photos.length]); // recréer si la liste change

  useEffect(() => {
    if (!user) { navigate("/connexion"); return; }
    getMyMedia()
      .then(setPhotos)
      .catch(() => showSnack("Impossible de charger la galerie.", "error"))
      .finally(() => setLoading(false));
  }, [user, navigate]); // eslint-disable-line

  useEffect(() => {
    if (!localStorage.getItem("tourDone")) {
      const t = setTimeout(startTour, 800);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev);
    setSelected([]);
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selected.length === photos.length) {
      setSelected([]);
    } else {
      setSelected(photos.map((p) => p.ID_media));
    }
  };

  const handleBulkDownload = async () => {
    if (selected.length === 0) return;
    setDownloading(true);
    try {
      if (selected.length === 1) {
        const img = photos.find((p) => p.ID_media === selected[0]);
        triggerSingleDownload(img?.public_id, img?.original_name);
      } else {
        await downloadMediaZip(selected);
      }
    } catch (e) {
      showSnack(e.message || "Erreur téléchargement", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPending(files.map((file) => ({ file, label: "" })));
    e.target.value = "";
  };

  const handleUpload = async () => {
    const total = pending.length;
    setUploadProgress({ done: 0, total });
    let errors = 0;
    const CONCURRENCY = 5;
    for (let i = 0; i < pending.length; i += CONCURRENCY) {
      const batch = pending.slice(i, i + CONCURRENCY);
      await Promise.allSettled(
        batch.map(async (it, batchIdx) => {
          const label = labelRefs.current[i + batchIdx]?.value || "";
          try {
            const { ID_media: id, public_id } = await uploadImage(it.file, "gallery", label);
            setPhotos((prev) => [{ ID_media: id, public_id, label, original_name: it.file.name, mime_type: it.file.type }, ...prev]);
          } catch {
            errors++;
          } finally {
            setUploadProgress((prev) => ({ ...prev, done: prev.done + 1 }));
          }
        })
      );
    }
    labelRefs.current = [];
    setPending([]);
    setUploadProgress(null);
    if (errors === 0) {
      showSnack(`${total} média(s) ajouté(s) !`, "success");
    } else {
      showSnack(`${total - errors} média(s) ajouté(s), ${errors} échec(s).`, "warning");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMedia(id);
      setPhotos((prev) => prev.filter((p) => p.ID_media !== id));
      setSelected((prev) => prev.filter((x) => x !== id));
      if (lightboxIdx !== null) setLightboxIdx(null);
      showSnack("Média supprimé.", "success");
    } catch (e) {
      showSnack(e.message || "Erreur suppression", "error");
    }
  };

  const handleLabelSaved = (id, label) => {
    setPhotos((prev) => prev.map((p) => p.ID_media === id ? { ...p, label } : p));
  };

  const col = theme.custom.mymodal;

  return (
    <>
      <Header />
      <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, sm: 3 }, pb: selectMode ? 12 : 3 }}>
        {/* Titre + boutons */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h5" sx={{ color: col.text, fontWeight: "bold" }}>
            Ma galerie
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {!selectMode && (
              <>
                <Button
                  data-tour="tour-add-media"
                  variant="outlined"
                  startIcon={<PermMediaIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }}
                >
                  Ajouter des médias
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
              </>
            )}
            {photos.length > 0 && (
              <Button
                variant={selectMode ? "contained" : "outlined"}
                startIcon={selectMode ? <CloseIcon /> : <CheckBoxOutlineBlankIcon />}
                onClick={toggleSelectMode}
                sx={selectMode
                  ? { bgcolor: col.button, color: col.text }
                  : { color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }
                }
              >
                {selectMode
                  ? `Annuler${selected.length > 0 ? ` (${selected.length})` : ""}`
                  : "Sélectionner"}
              </Button>
            )}
          </Box>
        </Box>

        {/* Zone pending uploads */}
        {(pending.length > 0 || uploadProgress) && (
          <Box sx={{ mb: 3, p: 2, border: `1px solid ${col.text}33`, borderRadius: 2, backgroundColor: col.main }}>
            {uploadProgress ? (
              /* ── Progression en cours ── */
              <Box>
                <Typography sx={{ color: col.text, fontWeight: 600, mb: 1 }}>
                  Envoi en cours… {uploadProgress.done} / {uploadProgress.total}
                </Typography>
                <Box sx={{ width: "100%", height: 6, bgcolor: col.text + "22", borderRadius: 3, overflow: "hidden" }}>
                  <Box sx={{
                    height: "100%", borderRadius: 3,
                    bgcolor: col.button,
                    width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`,
                    transition: "width 0.3s ease",
                  }} />
                </Box>
              </Box>
            ) : pending.length <= 10 ? (
              /* ── Peu de fichiers : affichage avec labels ── */
              <>
                <Typography sx={{ color: col.text, fontWeight: 600, mb: 1.5 }}>
                  {pending.length} média(s) à envoyer — ajoute un label si tu veux
                </Typography>
                {pending.map((it, idx) => (
                  <Box key={`${it.file.name}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ color: col.text, fontSize: "0.82rem", minWidth: 180, opacity: 0.8 }}>
                      {it.file.name}
                    </Typography>
                    <TextField
                      size="small" placeholder="Label (optionnel)"
                      defaultValue=""
                      inputRef={(el) => { labelRefs.current[idx] = el; }}
                      onBlur={(e) => console.log('[label blur] relatedTarget:', e.relatedTarget?.tagName, e.relatedTarget?.id, e.relatedTarget?.className?.slice?.(0, 60))}
                      sx={{
                        flex: 1, minWidth: 180,
                        "& input": { color: col.text, fontSize: "0.82rem" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" },
                        "& label": { color: col.text + "88" },
                      }}
                    />
                  </Box>
                ))}
              </>
            ) : (
              /* ── Beaucoup de fichiers : affichage compact ── */
              <>
                <Typography sx={{ color: col.text, fontWeight: 600, mb: 0.5 }}>
                  {pending.length} médias sélectionnés
                </Typography>
                <Typography sx={{ color: col.text, opacity: 0.6, fontSize: "0.83rem" }}>
                  Les labels peuvent être ajoutés après l'envoi. L'upload se fera en parallèle (5 à la fois).
                </Typography>
              </>
            )}

            {!uploadProgress && (
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  sx={{ backgroundColor: col.button, color: col.text }}
                >
                  Envoyer {pending.length} média{pending.length > 1 ? "s" : ""}
                </Button>
                <Button onClick={() => setPending([])} sx={{ color: col.text }}>
                  Annuler
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Galerie */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress sx={{ color: col.text }} />
          </Box>
        ) : photos.length === 0 ? (
          <Typography sx={{ color: col.text, opacity: 0.6, textAlign: "center", mt: 8 }}>
            Aucun média pour l'instant. Commence par en ajouter !
          </Typography>
        ) : (
          <>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
              gap: 2,
            }}>
              {photos.slice(0, visibleCount).map((img, idx) => (
                <PhotoCard
                  key={img.ID_media}
                  img={img}
                  theme={theme}
                  onDelete={handleDelete}
                  onLabelSaved={handleLabelSaved}
                  onZoom={() => setLightboxIdx(idx)}
                  onDownload={() => triggerSingleDownload(img.public_id, img.original_name)}
                  selectMode={selectMode}
                  isSelected={selected.includes(img.ID_media)}
                  onToggleSelect={() => toggleSelect(img.ID_media)}
                />
              ))}
            </Box>
            {/* Sentinelle infinite scroll */}
            {visibleCount < photos.length && (
              <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} sx={{ color: col.text, opacity: 0.4 }} />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Barre flottante mode sélection */}
      {selectMode && (
        <Box sx={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 200, display: "flex", alignItems: "center", gap: 1.5,
          bgcolor: col.main, border: `1px solid ${col.text}33`,
          borderRadius: 4, px: 3, py: 1.5, boxShadow: 8,
          backdropFilter: "blur(8px)",
        }}>
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={selectAll}
            sx={{ color: col.text, fontSize: "0.8rem" }}
          >
            {selected.length === photos.length ? "Désélectionner tout" : "Tout sélectionner"}
          </Button>
          <Box sx={{ width: "1px", height: 24, bgcolor: col.text + "33" }} />
          <Button
            variant="contained"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleBulkDownload}
            disabled={selected.length === 0 || downloading}
            sx={{ bgcolor: col.button, color: col.text, fontWeight: 600 }}
          >
            {selected.length > 1
              ? `Télécharger (${selected.length}) en ZIP`
              : selected.length === 1
              ? "Télécharger"
              : "Télécharger"}
          </Button>
        </Box>
      )}

      {lightboxIdx !== null && photos.length > 0 && !selectMode && (
        <Lightbox
          images={photos}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}

      {Snack}
    </>
  );
}
